import type { ResultsScore, Score } from '../pages/material/results/api/results';
import { SCORE_STATUS } from '../constants/ScoreStatus';
import { Medialink } from '../api/medialink';

export type Result = Score & {
  memberId: string;
  nodeId: string;
};

export type Status = typeof SCORE_STATUS[keyof typeof SCORE_STATUS];

export type ResultsForScorableItem = Pick<
  Result,
  'score' | 'session' | 'needsGrading' | 'duration' | 'completedBySystem' | 'isZeroScore' | 'achievedAt'
>;

/**
 * @deprecated should be retrieved from the backend
 */
export const getStatusForScorableItem = (scorableItem: ResultsForScorableItem): Status => {
  const { session, score, needsGrading, duration, completedBySystem, isZeroScore } = scorableItem;

  if (!session || (session && duration === 0 && score.maximum === 0) || isZeroScore) {
    return SCORE_STATUS.NOT_STARTED;
  }
  if (needsGrading) {
    return SCORE_STATUS.PENDING_GRADING;
  }
  if (session && score.achievable !== score.maximum) {
    return SCORE_STATUS.BUSY;
  }
  if (completedBySystem) {
    return SCORE_STATUS.NOT_FINISHED;
  }
  if (score.achievable === score.maximum) {
    return SCORE_STATUS.FINISHED;
  }

  throw new Error(`Unknown status for result: ${JSON.stringify(scorableItem)}.`);
};

export const getStatusLabel = (status: Status) => {
  return `PAGES.RESULTS.PROGRESS_BAR.STATUS.${status.toUpperCase()}.A11Y_LABEL`;
};

export const getResultPercentages = (score?: ResultsScore, needsGrading?: boolean) => {
  if (!score) return { awaiting: 0, success: 0, failure: 0, undone: 100 };

  if (needsGrading) return { awaiting: 100, success: 0, failure: 0, undone: 0 };

  return {
    awaiting: 0,
    success: score.achieved,
    failure: score.achievable - score.achieved,
    undone: score.maximum - score.achievable,
  };
};

export const getShowScore = (medialink: Medialink): boolean => {
  const showScore =
    medialink?.assessmentQExercise?.showScore ||
    medialink?.eduhintExercise?.showScore ||
    medialink?.edumaticExercise?.showScore;

  return Boolean(showScore);
};

export const formatDuration = (seconds: number): string => {
  if (seconds === 0) return '';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const remainingMinutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${remainingMinutes}m`;
};
